import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FilterLabel } from '@lt/components';
import { Star } from '@lt/components/icons-react/userflow';
import { i18n } from '@lt/localization';
import MultichoiceFilterGroup from '../components/MultichoiceFilterGroup';
import type { IFilter } from '../types';

const makeItems = (data) =>
  data.map((item) => {
    const { id: stars, selected, disabled } = item;
    const name = stars
      ? Array(stars).fill(
          <StyledStar $filled={selected} $disabled={disabled} />,
        )
      : i18n.t('filters.stars.other');

    return { ...item, name };
  });

const FilterStars = ({
  data,
  className = '',
  isMobile = false,
  onChange,
  onClear,
  currencyCode,
}: IFilter): JSX.Element => {
  const items = useMemo(() => makeItems(data), [data]);
  return (
    <MultichoiceFilterGroup
      data={items}
      className={className}
      isMobile={isMobile}
      onChange={onChange}
      onClear={onClear}
      Filter={FilterStars}
      LabelComponent={StyledFilterLabel}
      currencyCode={currencyCode}
    />
  );
};

const StyledStar = styled(Star)<{
  $filled: boolean;
  $disabled: boolean;
}>`
  margin-right: 2px;
  width: 13px;
  height: 13px;
  stroke-width: 1px;
  stroke: ${({ theme }) => theme.colors.blackLight};
  fill: transparent;

  ${({ $filled, $disabled, theme }) =>
    $filled &&
    `
      fill: ${$disabled ? theme.colors.greyLight : theme.colors.black};
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    `
      stroke: ${theme.colors.greyLight};
    `}
`;

const StyledFilterLabel = styled(FilterLabel)<{
  $disabled: boolean;
}>`
  &:hover {
    ${StyledStar} {
      ${({ $disabled, theme }) =>
        !$disabled &&
        `
          stroke: ${theme.colors.black};
        `}
    }
  }
`;

export default Object.assign(FilterStars, {
  filterName: 'stars',
  filterLabel: i18n.t('filters.stars.title'),
  valuesLabel: (data) =>
    data
      .filter((item) => item.selected)
      .map((item) => (item.id ? `${item.id}★` : i18n.t('filters.stars.other')))
      .reduce((prev: string, curr: string) => [prev, ', ', curr]),
});
